import React from "react";
import { TextField, TextFieldProps, InputAdornment } from "@material-ui/core";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import { styled } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";

// Define the styled search icon component
const StyledSearchIcon = styled(SearchIcon)<{ disabled?: boolean }>(
  ({ theme, disabled }) => ({
    color: disabled ? theme.palette.grey[400] : theme.palette.text.secondary,
    fontSize: "1.25rem",
  }),
);

// Styled TextField component with custom focus and hover styles
const StyledTextField = styled(TextField)(({ theme }) => ({
  [`& .MuiInputBase-root`]: {
    height: "42px !important", // Default height
    paddingRight: "0px !important",
    "&.Mui-focused": {
      borderColor: theme?.palette?.secondaryButtonColorCTABlue?.main, // Active focus border color
    },
  },
  [`& .MuiOutlinedInput-root`]: {
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme?.palette?.secondaryButtonColorCTABlue?.main, // Set focused outline border color
    },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: theme?.palette?.error?.main, // Error border color
    },
  },
  [`& .MuiFormHelperText-root`]: {
    marginBottom: "-10px !important",
  },
  [`& .MuiFormHelperText-root.Mui-error`]: {
    marginLeft: "1px !important",
    marginTop: "0px !important",
  },
  [`& .MuiInputAdornment-root`]: {
    cursor: "pointer", // Custom cursor for adornment
  },
}));

type TextFieldComponentProps = TextFieldProps & {
  textFieldId: string;
  classes?: ClassNameMap;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: React.Dispatch<React.SetStateAction<boolean>>;
  handleFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  handleBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholderText?: string;
  disabled?: boolean;
  variant?: "outlined" | "standard" | "filled";
  error?: boolean;
  helperText?: string;
  label?: string;
};

const TextFieldComponent: React.FC<TextFieldComponentProps> = (props) => {
  const {
    textFieldId,
    classes,
    onChange,
    value,
    placeholderText,
    onKeyDown,
    handleFocus,
    handleBlur,
    disabled,
    variant,
    error,
    helperText,
    label,
  } = props;

  switch (textFieldId) {
    case "SearchInput":
      return (
        <StyledTextField
          variant={variant ?? "outlined"}
          placeholder={placeholderText ?? "Search..."}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          error={error}
          helperText={helperText}
          label={label}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <StyledSearchIcon />
              </InputAdornment>
            ),
            classes: {
              root: classes?.root,
              input: classes?.input,
            },
          }}
          fullWidth
        />
      );

    default:
      return (
        <StyledTextField
          variant={variant ?? "outlined"}
          placeholder={placeholderText ?? "Enter value"}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled ?? false}
          fullWidth
        />
      );
  }
};

export default TextFieldComponent;
